import { useCameras } from "./useCameras";
import { Brand } from "./Brand";
import { useEffect, useRef, useState } from "react";
import {
  Camera,
  ImagePlus,
  Send,
  Check,
  RefreshCw,
  Video,
  X,
  Download,
} from "lucide-react";
import { WebcamAdapter } from "./Camera";

async function preparePhoto(file: File): Promise<File> {
  if (file.size > 80 * 1024 * 1024)
    throw new Error("Choose a photo under 80 MB.");
  if (["image/jpeg", "image/png", "image/webp"].includes(file.type))
    return file;
  // Some phones supply HEIC. Convert only when this browser can decode it.
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    if (image.naturalWidth * image.naturalHeight > 60_000_000)
      throw new Error();
    const scale = Math.min(
      1,
      2560 / Math.max(image.naturalWidth, image.naturalHeight),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    canvas
      .getContext("2d")!
      .drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.93),
    );
    if (!blob) throw new Error();
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
    });
  } catch {
    throw new Error(
      "This phone could not open that photo. Take a new photo or choose JPEG, PNG or WebP.",
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function CapturePage({ token }: { token: string }) {
  const [event, setEvent] = useState<{ name: string } | null>(null),
    [unavailable, setUnavailable] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [pending, setPending] = useState<File | null>(null),
    [preview, setPreview] = useState(""),
    [sent, setSent] = useState(false),
    [count, setCount] = useState(0),
    [cameraOn, setCameraOn] = useState(false),
    [cameraBusy, setCameraBusy] = useState(false),
    [facing, setFacing] = useState<"user" | "environment">("environment");
  const { devices, refresh: refreshCameras } = useCameras();
  const [deviceId, setDeviceId] = useState("");
  const nativeInput = useRef<HTMLInputElement>(null),
    filesInput = useRef<HTMLInputElement>(null),
    video = useRef<HTMLVideoElement>(null),
    adapter = useRef(new WebcamAdapter()),
    mounted = useRef(true),
    cameraGeneration = useRef(0),
    sending = useRef(false),
    checking = useRef(false);
  const canPreview = !!navigator.mediaDevices?.getUserMedia;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cameraGeneration.current++;
      adapter.current.stop();
    };
  }, []);
  useEffect(() => {
    if (!pending) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(pending);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);
  function stopCamera() {
    cameraGeneration.current++;
    adapter.current.stop();
    setCameraOn(false);
    setCameraBusy(false);
  }
  async function checkLink() {
    if (checking.current) return;
    checking.current = true;
    try {
      const response = await fetch(`/api/capture/${token}`, {
        credentials: "omit",
        signal: AbortSignal.timeout(8000),
      });
      if (!mounted.current) return;
      if (response.status === 404) {
        setUnavailable(true);
        stopCamera();
        setError(
          "This capture link is no longer active. Ask the event operator for a new one.",
        );
        return;
      }
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (mounted.current) {
        setEvent(data);
        setUnavailable(false);
      }
    } catch {
      if (mounted.current && !event)
        setError(
          "Connect to the event Wi-Fi and try again. The event server must be reachable.",
        );
    } finally {
      checking.current = false;
    }
  }
  useEffect(() => {
    void checkLink();
    const timer = setInterval(checkLink, 20000);
    window.addEventListener("online", checkLink);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", checkLink);
    };
  }, [token]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (pending) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending]);
  async function select(file?: File) {
    if (!file || sending.current) return;
    setBusy(true);
    setSent(false);
    setError("");
    try {
      const ready = await preparePhoto(file);
      if (mounted.current) {
        setPending(ready);
        stopCamera();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function startCamera(mode = facing, selectedDevice = deviceId) {
    const generation = ++cameraGeneration.current;
    setCameraBusy(true);
    setCameraOn(false);
    setError("");
    setFacing(mode);
    try {
      const stream = await adapter.current.start(
        selectedDevice || undefined,
        mode,
      );
      if (!mounted.current || generation !== cameraGeneration.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      if (video.current) {
        video.current.srcObject = stream;
        await video.current.play();
      }
      if (!mounted.current || generation !== cameraGeneration.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      setCameraOn(true);
      void refreshCameras();
      stream.getVideoTracks()[0]?.addEventListener("ended", () => {
        if (mounted.current && generation === cameraGeneration.current) {
          stopCamera();
          setError(
            "Camera disconnected. Select another camera or reconnect it.",
          );
          void refreshCameras();
        }
      });
    } catch (e) {
      if (mounted.current && generation === cameraGeneration.current) {
        adapter.current.stop();
        setError(
          (e as Error).message +
            " You can also use Take a photo or Choose photo.",
        );
      }
    } finally {
      if (mounted.current && generation === cameraGeneration.current)
        setCameraBusy(false);
    }
  }
  async function snap() {
    if (!video.current) return;
    try {
      const blob = await adapter.current.capture(video.current);
      await select(
        new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" }),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function submit() {
    if (!pending || sending.current || unavailable) return;
    sending.current = true;
    setBusy(true);
    setError("");
    const body = new FormData();
    body.append("file", pending);
    try {
      const response = await fetch(`/api/capture/${token}/upload`, {
        method: "POST",
        credentials: "omit",
        body,
        signal: AbortSignal.timeout(120000),
      });
      const data = await response
        .json()
        .catch(() => ({ error: "Upload failed. Please try again." }));
      if (response.status === 404) {
        setUnavailable(true);
        stopCamera();
      }
      if (!response.ok) throw new Error(data.error || "Upload failed.");
      if (mounted.current) {
        setPending(null);
        setSent(true);
        setCount((c) => c + 1);
      }
    } catch (e) {
      if (mounted.current)
        setError(
          `${(e as Error).message} Your photo is still here; keep this page open and retry.`,
        );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const disabled = busy || unavailable || !event;
  return (
    <main className="capture-page">
      <header className="capture-header">
        <Brand />
        <span className="pill">EVENT CAPTURE</span>
      </header>
      <div className="capture-intro">
        <p className="eyebrow">YOU'RE PART OF THE MOMENT</p>
        <h1>{event?.name || "Connect to your event"}</h1>
        <p>Take a photo. Make it part of the wall.</p>
      </div>
      <section className="capture-card">
        {sent && (
          <div className="capture-success" role="status">
            <span>
              <Check size={30} />
            </span>
            <h2>You're on the wall.</h2>
            <p>Your photo was submitted successfully. Ready for another?</p>
          </div>
        )}
        <div
          className={`capture-view ${cameraOn ? "camera-active" : ""} ${preview ? "has-preview" : ""}`}
        >
          <video
            ref={video}
            muted
            playsInline
            aria-label="Live camera preview"
            hidden={!cameraOn}
          />
          {preview && <img src={preview} alt="Your photo, ready to submit" />}
          {!preview && !cameraOn && !sent && (
            <div className="capture-placeholder">
              <Camera size={42} />
              <h2>A moment worth sharing.</h2>
              <p>Use your camera or choose a photo from this device.</p>
            </div>
          )}
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {(!event || unavailable) && (
          <button
            onClick={() => {
              setError("");
              void checkLink();
            }}
          >
            <RefreshCw size={16} /> Check connection / link
          </button>
        )}
        {pending ? (
          <>
            <div className="capture-review">
              <span>Looking good?</span>
              <small>{pending.name}</small>
            </div>
            <div className="capture-actions">
              <button
                className="primary"
                disabled={disabled}
                onClick={() => void submit()}
              >
                <Send size={18} />
                {busy ? "Submitting…" : "Submit to event"}
              </button>
              <button
                disabled={busy}
                onClick={() => {
                  setPending(null);
                  setError("");
                }}
              >
                <RefreshCw size={17} /> Retake / choose another
              </button>
            </div>
            {unavailable && preview && (
              <a className="button" href={preview} download={pending.name}>
                <Download size={16} /> Save your photo
              </a>
            )}
          </>
        ) : (
          <>
            <div className="capture-actions">
              <button
                className="primary"
                disabled={disabled}
                onClick={() => nativeInput.current?.click()}
              >
                <Camera size={19} /> Take a photo
              </button>
              <button
                disabled={disabled}
                onClick={() => filesInput.current?.click()}
              >
                <ImagePlus size={19} /> Choose photo
              </button>
            </div>
            {canPreview && (
              <div className="capture-live-actions">
                <label className="capture-camera-select">
                  Camera source
                  <select
                    aria-label="Capture camera source"
                    value={deviceId}
                    disabled={disabled || cameraBusy}
                    onChange={(e) => {
                      const id = e.target.value;
                      setDeviceId(id);
                      if (cameraOn) void startCamera(facing, id);
                    }}
                  >
                    <option value="">
                      Automatic · {facing === "user" ? "front" : "rear"} camera
                    </option>
                    {devices
                      .filter((d) => d.deviceId)
                      .map((d, i) => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || `Camera ${i + 1}`}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  disabled={cameraBusy}
                  onClick={() => void refreshCameras()}
                >
                  Refresh camera list
                </button>
                <small>
                  Allow camera access to see device names. External cameras must
                  be supported as webcams by this device and browser.
                </small>
                {cameraOn ? (
                  <>
                    <button
                      disabled={disabled}
                      className="primary"
                      onClick={() => void snap()}
                    >
                      <Camera size={16} /> Capture this moment
                    </button>
                    <button
                      disabled={disabled || cameraBusy}
                      onClick={() => {
                        setDeviceId("");
                        void startCamera(
                          facing === "environment" ? "user" : "environment",
                          "",
                        );
                      }}
                    >
                      <RefreshCw size={15} /> Switch camera
                    </button>
                    <button disabled={busy} onClick={stopCamera}>
                      <X size={15} /> Close camera
                    </button>
                  </>
                ) : (
                  <button
                    disabled={disabled || cameraBusy}
                    onClick={() => void startCamera()}
                  >
                    <Video size={16} />
                    {cameraBusy ? "Opening camera…" : "Open live camera"}
                  </button>
                )}
              </div>
            )}
            {!canPreview && (
              <p className="capture-help">
                On a phone, Take a photo opens your device’s camera where
                supported. For a live camera preview, open an HTTPS link.
              </p>
            )}
          </>
        )}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          ref={nativeInput}
          onChange={(e) => {
            void select(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <input
          type="file"
          accept="image/*"
          hidden
          ref={filesInput}
          onChange={(e) => {
            void select(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <p className="capture-help">
          Photos appear automatically for this event.{" "}
          {count > 0
            ? `${count} submitted from this session.`
            : "No admin sign-in needed."}
        </p>
      </section>
      <footer className="capture-footer">
        Stay on the event Wi-Fi until your photo is submitted.
        <br />
        Only share this capture link with people allowed to add photos.
      </footer>
    </main>
  );
}
